/**
 * Tenant user management client (§7.1).
 *
 * Every call here requires TENANT_ADMIN server-side; the UI hides the entry point for anyone else,
 * but the server is what actually enforces it.
 */

import { AuthError } from './auth';
import { authorizedFetch } from './cognito';
import { Role } from './types';

const API_BASE = '/api/v1';

export interface TenantUser {
  id: string;
  email: string;
  full_name: string;
  roles: Role[];
  status: 'ACTIVE' | 'DISABLED';
  last_seen_at?: string;
  can_read_phi: boolean;
}

/**
 * Every other client in the app goes through `authorizedFetch`; this one did not (§13.6 A8).
 *
 * That meant admin calls had no request timeout — so a stalled connection hung indefinitely with
 * no error — and no silent token refresh, so a token expiring mid-session redirected the tenant
 * admin through Cognito and lost whatever they were editing, in a screen where the rest of the
 * app would simply have carried on.
 */
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authorizedFetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    // `authorizedFetch` has already tried a silent refresh and, failing that, begun the redirect.
    throw new AuthError(401, 'Not signed in');
  }
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') message = body.detail;
    } catch {
      /* keep the generic message */
    }
    throw new AuthError(res.status, message);
  }
  return res.status === 204 ? (undefined as T) : (res.json() as Promise<T>);
}

export const fetchTenantUsers = () => call<TenantUser[]>('/tenant/users');

export const createTenantUser = (email: string, fullName: string, roles: Role[]) =>
  call<TenantUser>('/tenant/users', {
    method: 'POST',
    body: JSON.stringify({ email, full_name: fullName, roles }),
  });

export const updateTenantUserRoles = (id: string, roles: Role[]) =>
  call<TenantUser>(`/tenant/users/${id}`, { method: 'PATCH', body: JSON.stringify({ roles }) });

export const disableTenantUser = (id: string) =>
  call<TenantUser>(`/tenant/users/${id}/disable`, { method: 'POST' });

export const enableTenantUser = (id: string) =>
  call<TenantUser>(`/tenant/users/${id}/enable`, { method: 'POST' });

export const ALL_ROLES: Role[] = ['TENANT_ADMIN', 'TECHNICIAN', 'PHYSICIAN', 'CORPUS_REVIEWER'];

export const ROLE_LABEL: Record<Role, string> = {
  PLATFORM_OPS: 'Platform Ops',
  TENANT_ADMIN: 'Admin',
  TECHNICIAN: 'Technician',
  PHYSICIAN: 'Physician',
  CORPUS_REVIEWER: 'Corpus Reviewer',
  READ_ONLY: 'Read-only',
};


// ── Platform operations (§9.7) ─────────────────────────────────────────────────────────────────
//
// Managing customer organisations. Only PLATFORM_OPS reaches any of this, and the server is what
// enforces that — the UI hides the entry point, which is convenience rather than a control.

export type TenantStatus = 'ACTIVE' | 'SUSPENDED' | 'OFFBOARDING';

export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  jurisdiction: string;
  residency_region: string;
  status: TenantStatus;
  is_platform_operator: boolean;
  sla_hours: number;
  idle_logout_seconds: number;
  separation_of_duties_required: boolean;
  user_count: number;
  /** Studies ever registered, from the atomic per-tenant counter — not a live count. */
  studies_registered: number;
}

export interface PlatformAuditEntry {
  id: string;
  ts: string;
  action: string;
  actor_email: string | null;
  target_tenant_slug: string | null;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  reason: string | null;
}

export const listTenants = () => call<TenantSummary[]>('/platform/tenants');

export const createTenant = (body: {
  slug: string;
  name: string;
  jurisdiction?: string;
  sla_hours?: number;
  idle_logout_seconds?: number;
  separation_of_duties_required?: boolean;
}) => call<TenantSummary>('/platform/tenants', { method: 'POST', body: JSON.stringify(body) });

export const updateTenant = (
  id: string,
  body: Partial<Pick<TenantSummary,
    'name' | 'sla_hours' | 'idle_logout_seconds' | 'separation_of_duties_required'>>,
) => call<TenantSummary>(`/platform/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

/** Suspension and reactivation. There is no delete — see the endpoint's own docstring. */
export const setTenantStatus = (id: string, status: TenantStatus, reason: string) =>
  call<TenantSummary>(`/platform/tenants/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status, reason }),
  });

export const listTenantUsers = (id: string) =>
  call<TenantUser[]>(`/platform/tenants/${id}/users`);

/**
 * The first administrator of an organisation — the one account that cannot be created from
 * inside it. Always TENANT_ADMIN, which carries no PHI access: a platform operator cannot mint
 * clinical access in a customer's tenant, and the server ignores any roles sent here.
 */
export const provisionTenantAdmin = (id: string, body: {
  email: string; full_name: string; reason: string;
}) => call<TenantUser>(`/platform/tenants/${id}/admins`, {
  method: 'POST', body: JSON.stringify(body),
});

export const listPlatformAudit = () => call<PlatformAuditEntry[]>('/platform/audit');

// ── Validation: blinded audit windows and the corpus admission gate ───────────
//
// DESIGN.md §11.3(b), §11.4. Both surfaces work by **withholding** something, and the types here
// are deliberately shaped so the withheld field does not exist rather than merely going unused:
// a `submitted_label?: string` on the blind-read type would be one autocomplete away from being
// rendered, and the leak would be silent.

export interface AuditWindow {
  id: string;
  study_id: string;
  start_sample: number;
  end_sample: number;
  start_seconds: number;
  end_seconds: number;
  sample_rate_hz: number;
  status: 'PENDING' | 'READ' | 'SCORED';
  already_read: boolean;
}

export interface BlindFinding {
  class: string;
  start_sample: number;
  end_sample: number;
}

export interface WindowScore {
  per_class: Record<string, { true_positive: number; false_negative: number; false_positive: number }>;
  true_negative_window: boolean;
  reader_findings: number;
  engine_findings: number;
}

/** A candidate as the second reader sees it: no submitted label, no submitter. */
export interface BlindCandidate {
  id: string;
  study_id: string;
  start_sample: number;
  end_sample: number;
  start_seconds: number;
  end_seconds: number;
  channel: string | null;
  sample_rate_hz: number;
  strata: Record<string, unknown> | null;
  status: string;
}

/** After adjudication is reached, both reads are shown — the blinding has already done its work. */
export interface DisputedCandidate extends BlindCandidate {
  submitted_label: string;
  second_label: string;
}

export interface AuditSummary {
  windows_scored: number;
  windows_agreed_empty: number;
  windows_empty: number;
  per_class: Record<
    string,
    {
      true_positive: number;
      false_negative: number;
      false_positive: number;
      sensitivity: number | null;
      precision: number | null;
    }
  >;
}

export interface Agreement {
  read_twice: number;
  agreed?: number;
  agreement: number | null;
  disagreements?: Record<string, number>;
}

export const drawAuditWindows = (studyId: string) =>
  call<{ windows: AuditWindow[] }>(`/studies/${studyId}/audit-windows`, { method: 'POST' });

export const listAuditWindows = (studyId: string) =>
  call<{ windows: AuditWindow[] }>(`/studies/${studyId}/audit-windows`);

export const openAuditWindow = (windowId: string) =>
  call<AuditWindow>(`/audit-windows/${windowId}`);

export const submitBlindRead = (
  windowId: string,
  body: { findings: BlindFinding[]; found_nothing: boolean; notes?: string },
) =>
  call<{ id: string; status: string; score: WindowScore }>(`/audit-windows/${windowId}/read`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const auditSummary = (studyId?: string) =>
  call<AuditSummary>(`/audit/summary${studyId ? `?study_id=${studyId}` : ''}`);

export const corpusQueue = () =>
  call<{ candidates: BlindCandidate[]; counts: Record<string, number> }>('/corpus/queue');

export const adjudicationQueue = () =>
  call<{ candidates: DisputedCandidate[] }>('/corpus/adjudication');

export const submitSecondRead = (candidateId: string, label: string) =>
  call<{ id: string; status: string; agreed: boolean; submitted_label: string; second_label: string }>(
    `/corpus-candidates/${candidateId}/second-read`,
    { method: 'POST', body: JSON.stringify({ label }) },
  );

export const adjudicateCandidate = (
  candidateId: string,
  body: { admit: boolean; label?: string; reason: string },
) =>
  call<{ id: string; status: string }>(`/corpus-candidates/${candidateId}/adjudicate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const corpusAgreement = () => call<Agreement>('/corpus/agreement');

export const submitCorpusCandidate = (
  studyId: string,
  body: { start_sample: number; end_sample: number; label: string; channel?: string; run_id?: string },
) =>
  call<{ id: string; status: string; message: string }>(`/studies/${studyId}/corpus-candidates`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
